/**
 * Ideas for social posts, found without touching articles.
 *
 * Three sources, each named on every idea so a person can see where it came from:
 *   - results:     what your own posts showed (more of this, try this). Free; from our records.
 *   - competitors: what advertisers you watch are saying. Free; from ads already collected. Shown as a reference, never copied.
 *   - semrush:     what people search for, from one keyword lookup a manager runs on purpose (it spends Semrush units).
 *
 * Pure. An idea is a topic and a reason. It is never a finished post and never carries a number the person did not see.
 */
import { z } from "zod";
import { combinedVolume, type KeywordRow } from "./semrushKeywords.ts";
import type { NextItem } from "./socialInsights.ts";
import { cleanTopic } from "./socialStage.ts";

export const IDEA_SOURCES = ["results", "competitors", "semrush"] as const;
export const ideaSourceSchema = z.enum(IDEA_SOURCES);
export type IdeaSource = (typeof IDEA_SOURCES)[number];
export const IDEA_SOURCE_LABEL: Record<IdeaSource, string> = { results: "Your results", competitors: "Competitor ads", semrush: "Semrush" };

export const socialIdeaSchema = z.object({
  id: z.string(),
  source: ideaSourceSchema,
  /** What to post about, in a few words. Becomes the post's topic. */
  title: z.string(),
  /** The short topic to file the post under, or null when the idea is not a topic (a reaction to a competitor, a format test). */
  topic: z.string().nullable(),
  /** Why this is worth a post, in one sentence. */
  why: z.string(),
});
export type SocialIdea = z.infer<typeof socialIdeaSchema>;

export const socialIdeasSchema = z.object({ ideas: z.array(socialIdeaSchema), competitorsWatched: z.number().int() });
export type SocialIdeas = z.infer<typeof socialIdeasSchema>;

export const MAX_IDEAS = 8;

/** "More posts about X" and "Opening with a question instead of ..." become ideas. "Skip" items are advice not to post, so they are not ideas. */
export function ideasFromResults(next: readonly NextItem[]): SocialIdea[] {
  return next.filter((n) => n.kind !== "skip").map((n, i) => ({ id: `results-${i}`, source: "results" as const, title: n.title, topic: n.title.startsWith("More posts about ") ? cleanTopic(n.title.replace(/^More posts about /, "")) : null, why: n.reason }));
}

/** One idea per distinct headline, newest first. The headline is the competitor's own words, so it is shown as a reference only. */
export function ideasFromCompetitors(ads: ReadonlyArray<{ advertiser: string; headline: string }>, limit = 3): SocialIdea[] {
  const seen = new Set<string>();
  const out: SocialIdea[] = [];
  for (const a of ads) {
    const h = a.headline.trim();
    const key = h.toLowerCase();
    if (h.length < 8 || seen.has(key)) continue;
    seen.add(key);
    out.push({ id: `competitors-${out.length}`, source: "competitors", topic: null, title: `Your take on: ${h.length > 80 ? `${h.slice(0, 77)}…` : h}`, why: `${a.advertiser} is running this. Reply with your own point of view, not their words.` });
    if (out.length >= limit) break;
  }
  return out;
}

const QUESTION = /^(how|what|why|when|which|who|can|should|is|are|do|does)\b/i;

/** Questions people search for come first (they make the best posts), then the rest, each by searches a month in the US and Canada together. */
export function ideasFromKeywords(rows: readonly KeywordRow[], limit = 5): SocialIdea[] {
  const scored = rows
    .map((r) => ({ r, vol: combinedVolume(r), q: r.from.includes("question") || QUESTION.test(r.keyword) }))
    .filter((x) => x.vol !== null && x.vol > 0);
  scored.sort((a, b) => Number(b.q) - Number(a.q) || (b.vol ?? 0) - (a.vol ?? 0) || a.r.keyword.localeCompare(b.r.keyword));
  return scored.slice(0, limit).map((x, i) => ({
    id: `semrush-${i}`,
    source: "semrush" as const,
    title: x.r.keyword,
    topic: cleanTopic(x.r.keyword),
    why: `${x.vol!.toLocaleString("en-US")} searches a month in the US and Canada${x.q ? ", asked as a question" : ""}.`,
  }));
}

export function mergeIdeas(...lists: ReadonlyArray<readonly SocialIdea[]>): SocialIdea[] {
  const seen = new Set<string>();
  const out: SocialIdea[] = [];
  for (const i of lists.flat()) {
    const k = i.title.toLowerCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(i);
  }
  return out.slice(0, MAX_IDEAS);
}
